import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { FavoriteBorderOutlined } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import LanguageIcon from "@mui/icons-material/Language";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import { useTranslation } from "react-i18next";
import { HeaderSocials } from "../../data";
import routes from "../../utils";
import "../../i18n";

const getCurrencySymbol = (currency) => {
  switch (currency) {
    case 'AED': return 'د.إ';
    case 'USD': return '$';
    case 'EUR': return '€';
    default: return '$';
  }
};

const SocialLinkBar = () => {
  const navigate = useNavigate();
  const { currency, updateCurrency } = useContext(AuthContext);
  const { t, i18n } = useTranslation();

  const [openLanguage, setOpenLanguage] = useState(false);
  const [openCurrency, setOpenCurrency] = useState(false);
  const [language, setLanguage] = useState(localStorage.getItem("language") || "EN");

  // position: fixed coordinates for each dropdown
  const [currencyPos, setCurrencyPos] = useState({ top: 0, left: 0 });
  const [languagePos, setLanguagePos] = useState({ top: 0, left: 0 });

  const currencyRef = useRef(null);
  const languageRef = useRef(null);

  useEffect(() => {
    i18n.changeLanguage(language.toLowerCase());
  }, [language, i18n]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        currencyRef.current && !currencyRef.current.contains(event.target) &&
        languageRef.current && !languageRef.current.contains(event.target)
      ) {
        setOpenCurrency(false);
        setOpenLanguage(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reposition fixed dropdowns on scroll / resize
  useEffect(() => {
    const reposition = () => {
      if (openCurrency && currencyRef.current) {
        const rect = currencyRef.current.getBoundingClientRect();
        setCurrencyPos({ top: rect.bottom + 4, left: rect.left });
      }
      if (openLanguage && languageRef.current) {
        const rect = languageRef.current.getBoundingClientRect();
        setLanguagePos({ top: rect.bottom + 4, left: rect.left });
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [openCurrency, openLanguage]);

  const handleToggleCurrency = useCallback(() => {
    if (currencyRef.current) {
      const rect = currencyRef.current.getBoundingClientRect();
      setCurrencyPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpenCurrency((prev) => !prev);
    setOpenLanguage(false);
  }, []);

  const handleToggleLanguage = useCallback(() => {
    if (languageRef.current) {
      const rect = languageRef.current.getBoundingClientRect();
      setLanguagePos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpenLanguage((prev) => !prev);
    setOpenCurrency(false);
  }, []);

  const handleNavigate = () => {
    navigate(localStorage.getItem("token") ? "/user/wishlists" : "/user/login");
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang.toLowerCase());
    localStorage.setItem("language", lang);
    setOpenLanguage(false);
  };

  return (
    <Box
      style={{ borderBottom: "0.3px solid #f1f2f3" }}
      className="w-full flex justify-center flex-col md:flex-row md:justify-start items-start text-white h-fit gap-x-5 gap-y-2 md:gap-y-0 py-3 md:py-0 text-xs md:text-sm md:h-11"
    >
      {/* Left Section (Phone & Email) */}
         <Box className="hidden md:flex md:w-1/2 gap-x-4 lg:gap-x-8 justify-start md:ml-4 lg:ml-20 items-center h-full overflow-hidden">
  <Box className="flex gap-x-2 md:mr-4 lg:mr-10 flex-shrink-0">
    <HeadsetMicIcon className="text-[15px] text-gray-600" />
    <Typography className="text-[11px] poppins text-[#2858a3] whitespace-nowrap">
      <a href="tel:80066839" className="text-inherit no-underline">
        <span className="text-gray-600">Call us:</span>{" "}
        <span className="hidden lg:inline">800-NOVEX (66839)</span>
        <span className="lg:hidden">800-NOVEX</span>
      </a>
    </Typography>
  </Box>
        <Box className="flex gap-x-2  text-gray-600">
          <MailOutlinedIcon className="text-[15px]" />
          <Typography className="lowercase text-[11px] poppins">
            <a
              href="mailto:ecommerce@buraqstar.com"
              className="text-inherit no-underline"
            >
              ecommerce@buraqstar.com
            </a>
   
          </Typography>
        </Box>
      </Box>

      {/* Socials — hidden on tablet, visible on desktop */}
      <Box
        className="hidden lg:flex items-center h-full"
        sx={{ gap: "10px", flexShrink: 1, px: "8px" }}
      >
        {HeaderSocials.map((social, i) => (
          <Link
            target="_blank"
            to={social.link}
            key={i}
            style={{ color: "#6b7280", display: "flex", alignItems: "center" }}
          >
            {social.icon}
          </Link>
        ))}
      </Box>

      {/* Right Section */}
      <Box className="md:w-1/2 w-full flex justify-center items-center h-full">

        {/* Store Locator & Wishlist */}
 <Box className="hidden md:flex items-center gap-x-8 border-r-2 border-white md:pe-8 sm:pe-5 pe-0 ipad-store-wishlist text-gray-600">
  <Link to={routes.storeLocator}>
    <Box className="flex gap-x-2 cursor-pointer items-center">
      <LocationOnOutlinedIcon className="text-xl flex-shrink-0" />
      <Typography className="capitalize text-[11px] poppins whitespace-nowrap">
        {t("store_locations")}
      </Typography>
    </Box>
  </Link>

  <Box className="flex gap-x-2 cursor-pointer items-center" onClick={handleNavigate}>
    <FavoriteBorderOutlined className="text-xl flex-shrink-0" />
    <Typography className="capitalize text-[11px] poppins whitespace-nowrap">
      {t("wishlist")}
    </Typography>
  </Box>
</Box>

        {/* Currency & Language Selectors */}
        <Box className="flex items-center gap-x-3 md:gap-x-3 lg:gap-x-5 md:ps-3 lg:ps-5">

          {/* ── Currency Selector ── */}
          <Box ref={currencyRef}>
            <Button
              className="flex items-center text-gray-500 poppins text-xs min-w-0 px-2 md:px-3 py-1 rounded-lg transition-all duration-200 gap-1"
              onClick={handleToggleCurrency}
              sx={{
                backgroundColor: { xs: 'rgba(255,255,255,0.1)', md: 'transparent' },
                backdropFilter: { xs: 'blur(10px)', md: 'none' },
                border: { xs: '1px solid rgba(255,255,255,0.2)', md: 'none' },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                minWidth: { xs: '75px', md: 'auto' },
              }}
            >
              <Typography className="text-[11px] font-medium">{getCurrencySymbol(currency)}</Typography>
              <Typography className="text-[11px] font-medium">{currency}</Typography>
              {openCurrency
                ? <ArrowDropUpIcon sx={{ fontSize: '16px' }} />
                : <ArrowDropDownIcon sx={{ fontSize: '16px' }} />}
            </Button>

            {/* Dropdown — position:fixed escapes any overflow:hidden parent */}
            {openCurrency && (
              <Box
                sx={{
                  position: 'fixed',
                  top: currencyPos.top,
                  left: currencyPos.left,
                  zIndex: 9999,
                  backgroundColor: '#fff',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  overflow: 'hidden',
                  minWidth: '90px',
                }}
              >
                {["AED", "EUR", "USD"].map((curr, i) => (
                  <Box
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer text-[11px] font-medium transition-colors duration-150"
                    onClick={() => { updateCurrency(curr); setOpenCurrency(false); }}
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      backgroundColor: curr === currency ? '#e3f2fd' : 'transparent',
                      color: curr === currency ? '#1976d2' : '#333',
                    }}
                  >
                    <Typography className="text-[11px] font-medium">{getCurrencySymbol(curr)}</Typography>
                    <Typography className="text-[11px] font-medium">{curr}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* ── Language Selector ── */}
          <Box ref={languageRef}>
            <Button
              className="flex items-center text-gray-600 text-xs min-w-0 px-2 md:px-3 py-1 rounded-lg transition-all duration-200 gap-1"
              onClick={handleToggleLanguage}
              sx={{
                backgroundColor: { xs: 'rgba(255,255,255,0.1)', md: 'transparent' },
                backdropFilter: { xs: 'blur(10px)', md: 'none' },
                border: { xs: '1px solid rgba(255,255,255,0.2)', md: 'none' },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                minWidth: { xs: '70px', md: 'auto' },
              }}
            >
              <LanguageIcon sx={{ fontSize: '14px', opacity: 0.8 }} />
              <Typography className="poppins text-[11px] font-medium">{language}</Typography>
              {openLanguage
                ? <ArrowDropUpIcon sx={{ fontSize: '16px' }} />
                : <ArrowDropDownIcon sx={{ fontSize: '16px' }} />}
            </Button>

            {/* Dropdown — position:fixed escapes any overflow:hidden parent */}
            {openLanguage && (
              <Box
                sx={{
                  position: 'fixed',
                  top: languagePos.top,
                  left: languagePos.left,
                  zIndex: 9999,
                  backgroundColor: '#fff',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  overflow: 'hidden',
                  minWidth: '100px',
                }}
              >
                {[
                  { code: "EN", name: "English" },
                  { code: "AR", name: "العربية" },
                  { code: "UR", name: "اردو" },
                ].map((lang, i) => (
                  <Box
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer text-xs font-medium transition-colors duration-150"
                    onClick={() => handleLanguageChange(lang.code)}
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      backgroundColor: lang.code === language ? '#e3f2fd' : 'transparent',
                      color: lang.code === language ? '#1976d2' : '#333',
                    }}
                  >
                    <LanguageIcon sx={{ fontSize: '12px', opacity: 0.6 }} />
                    <Box className="flex flex-col items-start">
                      <Typography className="text-[11px] font-medium leading-tight">{lang.code}</Typography>
                      <Typography className="text-[10px] opacity-60 leading-tight">{lang.name}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default SocialLinkBar;